// Portfolio Administration — Loans registry table on the standard DataTable
// (sticky header, sortable columns, 25-row pagination, optional group-by-
// portfolio with per-group rollups). Rows link through to
// /portfolio-admin/loans/{loanId}; selection state lives in the parent.
//
// The Grade column is the LIVE health grade from the batch health-grades
// endpoint (same computation as the loan dashboard — never re-derived
// client-side); the old Rating column's static origination letter is
// demoted into the grade popover. Days late is the real schedule-derived
// value from the same batch payload.

import { Link, useNavigate } from "react-router-dom";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { cn } from "@/design-system/lib/utils";
import type { LoanListItemDto } from "@/api/portfolioAdmin/types";
import type { LoanGradeRowDto } from "@/api/portfolioAdmin/loanHealthApi";
import { LoanAccountStatePill } from "./LoanAccountStatePill";
import { GradeChip } from "./GradeChip";
import { formatCalendarDate } from "@/utils/datetime";

export interface LoanListRow {
  loan: LoanListItemDto;
  /** Batch health-grade row; undefined while the batch loads or when the
   *  loan id wasn't in the payload. */
  grade?: LoanGradeRowDto;
  portfolioName: string | null;
}

const NO_PORTFOLIO_GROUP = "No portfolio";

/** Sort rank: A best → F worst; ungraded sorts after everything. */
function gradeRank(row: LoanListRow): number {
  const g = row.grade?.grade.grade;
  if (!g) return 99;
  return ["A", "B", "C", "D", "F"].indexOf(g);
}

function rankToLetter(rank: number): string {
  return ["A", "B", "C", "D", "F"][Math.min(Math.max(Math.round(rank), 0), 4)];
}

function formatMoney(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

export interface LoansListTableProps {
  rows: LoanListRow[];
  gradesLoading: boolean;
  loading?: boolean;
  error?: string | null;
  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (next: string[]) => void;
  groupByPortfolio?: boolean;
}

export function LoansListTable({
  rows,
  gradesLoading,
  loading,
  error,
  selectable = false,
  selectedIds,
  onSelectionChange,
  groupByPortfolio = false,
}: LoansListTableProps) {
  const navigate = useNavigate();

  const columns: Column<LoanListRow>[] = [
    {
      key: "loanNumber",
      header: "Loan ID",
      sortable: true,
      sortAccessor: (r) => r.loan.loanNumber,
      render: (r) => (
        <span className="text-gold-shadow hover:underline font-semibold">
          {r.loan.loanNumber}
        </span>
      ),
    },
    {
      key: "borrower",
      header: "Borrower",
      sortable: true,
      sortAccessor: (r) => r.loan.borrowerName ?? "",
      render: (r) => r.loan.borrowerName ?? "—",
    },
    {
      key: "grade",
      header: "Grade",
      sortable: true,
      sortAccessor: gradeRank,
      render: (r) => (
        <GradeChip
          grade={r.grade?.grade}
          disciplines={r.grade?.disciplines}
          originationRating={r.loan.creditRating}
          loading={gradesLoading && !r.grade}
          data-testid={`loans-grade-${r.loan.id}`}
        />
      ),
    },
    {
      key: "currentBalance",
      header: "Current balance",
      className: "text-right tabular-nums",
      sortable: true,
      sortAccessor: (r) => r.loan.currentBalance,
      render: (r) => formatMoney(r.loan.currentBalance),
    },
    {
      key: "original",
      header: "Original",
      className: "text-right tabular-nums",
      sortable: true,
      sortAccessor: (r) => r.loan.originalLoanAmount,
      render: (r) => formatMoney(r.loan.originalLoanAmount),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <LoanAccountStatePill state={r.loan.accountState} />,
    },
    {
      key: "daysLate",
      header: "Days late",
      className: "text-right tabular-nums",
      sortable: true,
      sortAccessor: (r) => r.grade?.daysDelinquent ?? -1,
      render: (r) => {
        const days = r.grade?.daysDelinquent;
        if (days == null) {
          return <span className="text-gray-mid">{gradesLoading ? "…" : "—"}</span>;
        }
        return (
          <span
            className={cn(
              days >= 30
                ? "text-status-error font-semibold"
                : days >= 1
                  ? "text-status-warning font-semibold"
                  : undefined,
            )}
          >
            {days}
          </span>
        );
      },
    },
    {
      key: "nextPayment",
      header: "Next payment",
      sortable: true,
      sortAccessor: (r) =>
        r.grade?.nextDueDate ? new Date(r.grade.nextDueDate) : null,
      render: (r) =>
        r.grade?.nextDueDate ? (
          <span className="text-small text-gray-mid tabular-nums">
            {formatCalendarDate(r.grade.nextDueDate)}
          </span>
        ) : (
          <span className="text-gray-mid">{gradesLoading ? "…" : "—"}</span>
        ),
    },
    {
      key: "maturity",
      header: "Maturity",
      sortable: true,
      sortAccessor: (r) => new Date(r.loan.maturityDate),
      render: (r) => (
        <span className="text-small text-gray-mid tabular-nums">
          {formatCalendarDate(r.loan.maturityDate) || "—"}
        </span>
      ),
    },
    {
      key: "b1",
      header: "B1",
      sortable: true,
      sortAccessor: (r) => r.loan.assignedB1UserName ?? "",
      render: (r) =>
        r.loan.assignedB1UserName ?? (
          <span className="text-gray-mid italic">Unassigned</span>
        ),
    },
    {
      key: "portfolio",
      header: "Portfolio",
      sortable: true,
      sortAccessor: (r) => r.portfolioName ?? "",
      render: (r) =>
        r.loan.portfolioId && r.portfolioName ? (
          <Link
            to={`/portfolio-admin/portfolios/${r.loan.portfolioId}`}
            className="text-gold-shadow hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {r.portfolioName}
          </Link>
        ) : (
          <span className="text-gray-mid">—</span>
        ),
    },
  ];

  return (
    <DataTable<LoanListRow>
      columns={columns}
      rows={rows}
      loading={loading}
      error={error}
      emptyMessage="No loans match these filters yet."
      rowKey={(r) => r.loan.id}
      selectedIds={selectable ? selectedIds : undefined}
      onSelectionChange={selectable ? onSelectionChange : undefined}
      sortable
      pagination="auto"
      stickyHeader
      onRowClick={(r) => navigate(`/portfolio-admin/loans/${r.loan.id}`)}
      rowTestId={(r) => `loans-row-${r.loan.id}`}
      data-testid="loans-list-table"
      groupBy={
        groupByPortfolio ? (r) => r.portfolioName ?? NO_PORTFOLIO_GROUP : undefined
      }
      groupLabel={(key, groupRows) => <PortfolioGroupLabel name={key} rows={groupRows} />}
      groupActions={(key, groupRows) => {
        const portfolioId =
          key === NO_PORTFOLIO_GROUP ? null : groupRows[0]?.loan.portfolioId;
        return portfolioId ? (
          <Link
            to={`/portfolio-admin/portfolios/${portfolioId}`}
            className="text-caption text-gold-shadow hover:underline normal-case tracking-normal"
            onClick={(e) => e.stopPropagation()}
            data-testid={`loans-group-link-${portfolioId}`}
          >
            View portfolio →
          </Link>
        ) : null;
      }}
    />
  );
}

/** Per-portfolio rollups in the group header: loan count is rendered by
 *  DataTable itself; this adds balance, worst + average grade, and the
 *  delinquent count (ungraded loans excluded from the grade rollups). */
function PortfolioGroupLabel({ name, rows }: { name: string; rows: LoanListRow[] }) {
  const balance = rows.reduce((sum, r) => sum + r.loan.currentBalance, 0);
  const ranks = rows.map(gradeRank).filter((rank) => rank < 99);
  const worst = ranks.length ? rankToLetter(Math.max(...ranks)) : null;
  const avg = ranks.length
    ? rankToLetter(ranks.reduce((a, b) => a + b, 0) / ranks.length)
    : null;
  const delinquent = rows.filter((r) => (r.grade?.daysDelinquent ?? 0) > 0).length;

  return (
    <span data-testid={`loans-group-label-${name}`}>
      {name}
      <span className="text-gray-mid font-normal normal-case tracking-normal">
        {" "}
        · {formatMoney(balance)}
        {worst && avg && ` · worst ${worst} / avg ${avg}`}
        {delinquent > 0 && (
          <span className="text-status-error"> · {delinquent} delinquent</span>
        )}
      </span>
    </span>
  );
}
